import { useAppContext } from "@/app/providers/AppContext";
import { getDefaultsByIntent } from "@/domains/watches/hooks/useWatchDefaults";
import { useWatchRecipeState } from "@/domains/watches/hooks/useWatchRecipeState";
import { getContractExpiryInfo, getMonitoringWindowState } from "@/domains/watches/lib/watchMonitoringWindow";
import { buildWatchRequestBody } from "@/domains/watches/lib/watchPayloads";
import type { AlertMode, RecipePreview } from "@/domains/watches/lib/watchTypes";
import type { FlowTrade } from "@/shared/lib/types";
import { Button } from "@/shared/ui/button";
import { Card, Mono, T } from "@/shared/ui/DesignLib";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/ui/select";
import { BellPlus, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

type WatchIntent = "good_time_to_buy" | "good_time_to_sell";

type LockedWatchSelection = {
  ticker: string;
  optionContract: string;
  contractType: "CALL" | "PUT" | "-";
  strike: string;
  expiry: string;
  side: string;
};

type AddToWatchlistDialogProps = {
  open: boolean;
  trade: FlowTrade | null;
  onOpenChange: (open: boolean) => void;
};

type WatchPreviewResponse = {
  recipe?: RecipePreview;
  [key: string]: unknown;
};

const sensitivityOptions = [
  { label: "Low", value: 0.5, desc: "More alerts" },
  { label: "Balanced", value: 0.7, desc: "Recommended" },
  { label: "High", value: 0.9, desc: "Fewer, sharper" },
] as const;

const intentOptions: Array<{ label: string; value: WatchIntent; desc: string }> = [
  {
    label: "Buy signal",
    value: "good_time_to_buy",
    desc: "Look for an entry setup",
  },
  {
    label: "Sell signal",
    value: "good_time_to_sell",
    desc: "Look for an exit setup",
  },
];

const toDisplayValue = (value: unknown) =>
  value === null || value === undefined || value === "" ? "-" : String(value);

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

const normalizeTicker = (trade: FlowTrade | null) => {
  const ticker = toDisplayValue(trade?.ticker).trim().toUpperCase();
  if (ticker && ticker !== "-") return ticker;

  const contract = getOptionContract(trade);
  const match = contract.match(/^O:([A-Z]+)\d{6}[CP]\d{8}$/);
  return match?.[1] || "";
};

const normalizeOccCandidate = (value: unknown) => {
  const raw = toDisplayValue(value).trim().toUpperCase();
  if (!raw || raw === "-") return "";
  const withoutPrefix = raw.startsWith("O:") ? raw.slice(2) : raw;
  if (/^[A-Z]{1,6}\d{6}[CP]\d{8}$/.test(withoutPrefix)) {
    return `O:${withoutPrefix}`;
  }
  return "";
};

function buildOccFromTradeFields(trade: FlowTrade | null) {
  const ticker = toDisplayValue(trade?.ticker).trim().toUpperCase();
  const exp = toDisplayValue(trade?.exp).trim();
  const side = toDisplayValue(trade?.c_p).trim().toUpperCase();
  const strike = Number(trade?.strike_price);

  if (!ticker || ticker === "-" || !/^\d{6}$/.test(exp) || !["C", "P"].includes(side)) {
    return "";
  }
  if (!Number.isFinite(strike)) return "";

  const strikePadded = String(Math.round(strike * 1000)).padStart(8, "0");
  return `O:${ticker}${exp}${side}${strikePadded}`;
}

function getOptionContract(trade: FlowTrade | null) {
  const candidates = [
    trade?.symbol_full,
    trade?.contract,
    trade?.formatted_string,
    trade?.symbol,
  ];
  for (const candidate of candidates) {
    const normalized = normalizeOccCandidate(candidate);
    if (normalized) return normalized;
  }
  return buildOccFromTradeFields(trade);
}

function formatExpiry(exp: unknown) {
  const raw = toDisplayValue(exp).trim();
  if (!/^\d{6}$/.test(raw)) return raw || "-";

  const yy = Number(raw.slice(0, 2));
  const yyyy = yy < 50 ? 2000 + yy : 1900 + yy;
  return `${raw.slice(2, 4)}/${raw.slice(4, 6)}/${yyyy}`;
}

function getLockedSelection(trade: FlowTrade | null): LockedWatchSelection {
  const optionContract = getOptionContract(trade);
  const ticker = normalizeTicker(trade);
  const side = toDisplayValue(trade?.bur_or_sell).replace(/_/g, " ");
  const contractSide = toDisplayValue(trade?.c_p).toUpperCase();

  return {
    ticker,
    optionContract,
    contractType:
      contractSide === "C" ? "CALL" : contractSide === "P" ? "PUT" : "-",
    strike: toDisplayValue(trade?.strike_price),
    expiry: formatExpiry(trade?.exp),
    side,
  };
}

const intentForTrade = (trade: FlowTrade | null): WatchIntent =>
  trade?.bur_or_sell === "SOLD" ? "good_time_to_sell" : "good_time_to_buy";

export default function AddToWatchlistDialog({
  open,
  trade,
  onOpenChange,
}: AddToWatchlistDialogProps) {
  const { api } = useAppContext();
  const selection = useMemo(() => getLockedSelection(trade), [trade]);
  const intent = intentForTrade(trade);
  const defaults = useMemo(() => getDefaultsByIntent(intent), [intent]);
  const [duration, setDuration] = useState(defaults.policy.max_duration_days);
  const [interval, setInterval] = useState(defaults.policy.evaluation_interval_minutes);
  const [marketHoursOnly, setMarketHoursOnly] = useState(defaults.policy.market_hours_only);
  const [alertMode, setAlertMode] = useState<AlertMode>(defaults.alertMode);
  const [cooldown, setCooldown] = useState(defaults.policy.cooldown_minutes);
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.7);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    recipeConditions,
    previewRecipe,
    syncRecipeState,
    recipeGroupViews,
    hasValidRecipeSelection,
    toggleRecipeCondition,
  } = useWatchRecipeState();

  const contractExpiryInfo = getContractExpiryInfo({
    contractMode: "manual",
    contractExpiry: "",
    manualOCC: selection.optionContract,
  });
  const lockToExpiryWindow = Boolean(
    contractExpiryInfo && contractExpiryInfo.dte < 5,
  );
  // Preserve the user's preferred duration in state, but never submit or display
  // a monitoring window that extends beyond a soon-expiring contract.
  const effectiveDuration = lockToExpiryWindow
    ? contractExpiryInfo!.dte
    : duration;

  const {
    monitoringWindowOptions,
    isCustomWindowSelected,
    suggestedCustomWindowDays,
    monitoringWindowSummary,
    isMonitoringWindowActive,
  } = getMonitoringWindowState({
    duration: effectiveDuration,
    contractExpiryInfo,
  });
  const isMonitoringWindowDisabled = (days: number) =>
    lockToExpiryWindow && days > (contractExpiryInfo?.dte || 0);

  const applyAlertModeDefaults = (mode: AlertMode) => {
    setAlertMode(mode);
    if (mode === "one_and_done") {
      setCooldown(0);
    } else {
      setCooldown(120);
    }
  };

  const closeDialog = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
  };

  const buildRequestBody = () =>
    buildWatchRequestBody({
      ticker: selection.ticker,
      intent,
      intentRequiresContract: true,
      contractMode: "manual",
      contractType: selection.contractType === "PUT" ? "put" : "call",
      contractExpiry: "",
      contractStrike: "",
      manualOCC: selection.optionContract,
      duration: effectiveDuration,
      interval,
      maxAlerts: alertMode === "one_and_done" ? 1 : 20,
      cooldown,
      marketHoursOnly,
      confidenceThreshold,
      ivThreshold: 80,
      targetPrice: null,
      targetDirection: "above",
      volatilitySensitivity: "medium",
      alertMode,
      conditions: [],
      conditionLogic: "AND",
      recipeConditions,
    });

  const handleReviewWatch = async () => {
    if (!selection.ticker || !selection.optionContract) {
      toast.error("Selected trade is missing a valid option contract");
      return;
    }

    try {
      setIsPreviewLoading(true);
      const preview = await api<WatchPreviewResponse>(
        "POST",
        "/api/watches/preview",
        buildRequestBody(),
      );
      syncRecipeState(preview?.recipe || null);
      setShowConfirmation(true);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, "Failed to preview watch"));
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selection.ticker || !selection.optionContract) {
      toast.error("Selected trade is missing a valid option contract");
      return;
    }

    try {
      setIsSubmitting(true);
      await api("POST", "/api/watches", buildRequestBody());
      toast.success(`Added ${selection.ticker} watch`);
      closeDialog(false);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, "Failed to add watch"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogContent
        className="bg-background border border-border text-foreground w-[92%] md:w-full max-w-2xl"
        style={{
          display: "flex",
          flexDirection: "column",
          maxHeight: "87dvh",
          height: "auto",
          overflow: "hidden",
          padding: 0,
        }}
      >
        <DialogHeader>
          <DialogTitle className="font-syne px-6 pt-4 text-xl font-bold tracking-tight dark:text-white text-black/90">
            Create New Market Watch
          </DialogTitle>
        </DialogHeader>

        <div
          style={{
            overflowY: "auto",
            flex: 1,
            padding: "0 24px 24px 24px",
            overscrollBehavior: "contain",
          }}
        >
          <div className="space-y-6">
            {showConfirmation ? (
              <div className="space-y-6">
                <button
                  type="button"
                  onClick={() => setShowConfirmation(false)}
                  style={{
                    background: "none",
                    border: "none",
                    color: T.dim,
                    cursor: "pointer",
                    fontFamily: T.mono,
                    fontSize: "0.58rem",
                    padding: 0,
                  }}
                >
                  &lt;- Back to form
                </button>

                <div>
                  <div
                    style={{
                      fontFamily: T.serif,
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: T.text,
                      marginBottom: 6,
                    }}
                  >
                    Here's what happens next
                  </div>
                  <Mono size=".6rem" color={T.dim}>
                    Your watch will monitor the enabled backend recipe conditions below.
                  </Mono>
                </div>

                <div
                  style={{
                    background: "rgba(0,184,219,0.03)",
                    borderLeft: `2px solid ${T.primary}`,
                    borderRadius: "0 6px 6px 0",
                    padding: "16px 18px",
                  }}
                >
                  {isPreviewLoading ? (
                    <Mono size=".65rem" color={T.dim}>
                      Refreshing watch preview...
                    </Mono>
                  ) : previewRecipe ? (
                    <div className="space-y-4">
                      <div>
                        <Mono size=".65rem" color={T.text} style={{ fontWeight: 600 }}>
                          {previewRecipe.label}
                        </Mono>
                        <Mono size=".58rem" color={T.dim} style={{ display: "block", marginTop: 4 }}>
                          {previewRecipe.description}
                        </Mono>
                      </div>
                      {recipeGroupViews.map((group) => (
                        <div key={group.id} className="space-y-2">
                          <div>
                            <Mono size=".6rem" color={T.text} style={{ fontWeight: 600 }}>
                              {group.label}
                            </Mono>
                            <Mono size=".52rem" color={T.dim} style={{ display: "block", marginTop: 2 }}>
                              {group.description}
                              {group.id === "core" && group.selection_mode === "at_least_one"
                                ? " At least one enabled setup trigger must pass."
                                : ""}
                            </Mono>
                          </div>
                          {group.conditions.map((condition) => (
                            <div
                              key={condition.id}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "20px minmax(0, 1fr)",
                                columnGap: 12,
                                alignItems: "start",
                              }}
                            >
                              <button
                                type="button"
                                disabled={condition.required}
                                onClick={() => toggleRecipeCondition(condition.id)}
                                style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 4,
                                  border: `1px solid ${condition.enabled ? T.primary : T.borderSoft}`,
                                  background: condition.enabled ? `${T.primary}20` : "transparent",
                                  color: condition.enabled ? T.primary : T.dim,
                                  cursor: condition.required ? "not-allowed" : "pointer",
                                  fontFamily: T.mono,
                                  fontSize: "0.42rem",
                                  lineHeight: 1,
                                }}
                              >
                                {condition.enabled ? "on" : ""}
                              </button>
                              <div style={{ minWidth: 0 }}>
                                <Mono size=".68rem" color={T.text}>
                                  {condition.label}
                                  {condition.required ? " (required)" : ""}
                                </Mono>
                                {condition.description && (
                                  <div
                                    style={{
                                      fontFamily: T.mono,
                                      fontSize: "0.6rem",
                                      color: T.dim,
                                      marginTop: 2,
                                    }}
                                  >
                                    {condition.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                      {!hasValidRecipeSelection && (
                        <Mono size=".55rem" color={T.red}>
                          At least one trade-setup trigger must remain enabled.
                        </Mono>
                      )}
                    </div>
                  ) : (
                    <Mono size=".65rem" color={T.dim}>
                      No canonical recipe is available for this watch in the current rollout mode.
                    </Mono>
                  )}
                </div>

                <div className="space-y-3">
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                    <Mono size=".79rem" color={T.dim}>
                      Monitoring window
                    </Mono>
                    <Mono size=".79rem" color={T.text} style={{ textAlign: "right" }}>
                      {monitoringWindowSummary}
                    </Mono>
                  </div>
                  <Mono
                    size=".55rem"
                    color={T.dim}
                    style={{ marginLeft: "auto", maxWidth: "85%", textAlign: "right", lineHeight: 1.3 }}
                  >
                    * Contract-based watches stop at expiry even if you pick a longer monitoring window.
                  </Mono>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                    <Mono size=".79rem" color={T.dim}>
                      Alert mode
                    </Mono>
                    <Mono size=".79rem" color={T.text}>
                      {alertMode === "one_and_done" ? "Single alert, then done" : "Repeat signals"}
                    </Mono>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                    <Mono size=".79rem" color={T.dim}>
                      Market hours
                    </Mono>
                    <Mono size=".79rem" color={T.text}>
                      {marketHoursOnly ? "9:30 AM - 4:00 PM ET" : "24/7"}
                    </Mono>
                  </div>
                </div>

                <Button
                  className="w-full py-3 text-base font-semibold"
                  onClick={handleSubmit}
                  disabled={isSubmitting || isPreviewLoading || !hasValidRecipeSelection}
                >
                  {isSubmitting ? "Creating..." : "Start the Watch"}
                </Button>
              </div>
            ) : (
              <>
            <Card style={{ padding: 14 }}>
              <div className="mono-label" style={{ marginBottom: 10 }}>
                Selected flow
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="mono-label" style={{ marginBottom: 6 }}>
                    Ticker
                  </div>
                  <Input value={selection.ticker} disabled className="lh-input font-mono" />
                </div>
                <div>
                  <div className="mono-label" style={{ marginBottom: 6 }}>
                    Flow side
                  </div>
                  <Input value={selection.side} disabled className="lh-input font-mono" />
                </div>
                <div className="md:col-span-2">
                  <div className="mono-label" style={{ marginBottom: 6 }}>
                    Intent
                  </div>
                  <Select value={intent} disabled>
                    <SelectTrigger className="lh-input w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {intentOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Mono size=".52rem" color={T.dim} style={{ display: "block", marginTop: 6 }}>
                    {intentOptions.find((option) => option.value === intent)?.desc}
                  </Mono>
                </div>
                <div className="md:col-span-2">
                  <div className="mono-label" style={{ marginBottom: 6 }}>
                    Contract
                  </div>
                  <Input value={selection.optionContract} disabled className="lh-input font-mono" />
                </div>
                <div>
                  <div className="mono-label" style={{ marginBottom: 6 }}>
                    Type
                  </div>
                  <Input value={selection.contractType} disabled className="lh-input font-mono" />
                </div>
                <div>
                  <div className="mono-label" style={{ marginBottom: 6 }}>
                    Strike / Expiry
                  </div>
                  <Input
                    value={`${selection.strike} / ${selection.expiry}`}
                    disabled
                    className="lh-input font-mono"
                  />
                </div>
              </div>
            </Card>

            <div>
              <div className="mono-label">Monitoring window</div>
              <Mono size=".52rem" color={T.dim} style={{ display: "block", marginTop: 4 }}>
                Choose how long to keep this setup live.
              </Mono>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(168px, 1fr))",
                  gap: 8,
                  marginTop: 10,
                }}
              >
                {monitoringWindowOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    disabled={isMonitoringWindowDisabled(opt.days)}
                    onClick={() => {
                      if (!isMonitoringWindowDisabled(opt.days)) setDuration(opt.days);
                    }}
                    style={{
                      minWidth: 0,
                      padding: "10px 12px",
                      borderRadius: 6,
                      border: isMonitoringWindowActive(opt.days)
                        ? `1px solid ${T.primary}`
                        : `1px solid ${T.borderSoft}`,
                      background: isMonitoringWindowActive(opt.days)
                        ? "rgba(0,184,219,0.08)"
                        : T.card2,
                      cursor: isMonitoringWindowDisabled(opt.days)
                        ? "not-allowed"
                        : "pointer",
                      opacity: isMonitoringWindowDisabled(opt.days) ? 0.45 : 1,
                      transition: "all 0.15s",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: T.mono,
                        fontSize: "0.66rem",
                        color: isMonitoringWindowActive(opt.days) ? T.primary : T.text,
                        lineHeight: 1.3,
                      }}
                    >
                      {opt.label}
                    </div>
                    <div
                      style={{
                        fontFamily: T.mono,
                        fontSize: "0.54rem",
                        color: T.dim,
                        marginTop: 4,
                        lineHeight: 1.35,
                      }}
                    >
                      {opt.helper}
                    </div>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    if (!lockToExpiryWindow && !isCustomWindowSelected) {
                      setDuration(suggestedCustomWindowDays);
                    }
                  }}
                  disabled={lockToExpiryWindow}
                  style={{
                    minWidth: 0,
                    padding: "10px 12px",
                    borderRadius: 6,
                    border: isCustomWindowSelected
                      ? `1px solid ${T.primary}`
                      : `1px solid ${T.borderSoft}`,
                    background: isCustomWindowSelected
                      ? "rgba(0,184,219,0.08)"
                      : T.card2,
                    cursor: lockToExpiryWindow ? "not-allowed" : "pointer",
                    opacity: lockToExpiryWindow ? 0.45 : 1,
                    transition: "all 0.15s",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontFamily: T.mono,
                      fontSize: "0.66rem",
                      color: isCustomWindowSelected ? T.primary : T.text,
                      lineHeight: 1.3,
                    }}
                  >
                    Custom window
                  </div>
                  <div
                    style={{
                      fontFamily: T.mono,
                      fontSize: "0.54rem",
                      color: T.dim,
                      marginTop: 4,
                      lineHeight: 1.35,
                    }}
                  >
                    Set an exact number of days
                  </div>
                </button>
              </div>
              {isCustomWindowSelected && (
                <div
                  style={{
                    marginTop: 10,
                    padding: 12,
                    borderRadius: 8,
                    border: `1px solid ${T.borderSoft}`,
                    background: T.card2,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <Mono size=".58rem" color={T.text}>
                      Custom monitoring window
                    </Mono>
                    <Mono size=".52rem" color={T.dim}>
                      {monitoringWindowSummary}
                    </Mono>
                  </div>
                  <Input
                    type="number"
                    className="lh-input max-w-[180px]"
                    value={effectiveDuration}
                    onChange={(event) => setDuration(parseInt(event.target.value) || 1)}
                    min={1}
                    max={30}
                    disabled={lockToExpiryWindow}
                  />
                </div>
              )}
            </div>

            <div>
              <div className="mono-label">After first alert</div>
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                {(
                  [
                    { label: "Done", desc: "One alert, then complete", mode: "one_and_done" },
                    { label: "Keep watching", desc: "Continue for repeat signals", mode: "keep_watching" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.mode}
                    type="button"
                    onClick={() => applyAlertModeDefaults(opt.mode)}
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      borderRadius: 6,
                      border:
                        alertMode === opt.mode
                          ? `1px solid ${T.primary}`
                          : `1px solid ${T.borderSoft}`,
                      background:
                        alertMode === opt.mode ? "rgba(0,184,219,0.08)" : T.card2,
                      cursor: "pointer",
                      transition: "all 0.15s",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: T.mono,
                        fontSize: ".78rem",
                        fontWeight: 600,
                        color: alertMode === opt.mode ? T.primary : T.text,
                      }}
                    >
                      {opt.label}
                    </div>
                    <div
                      style={{
                        fontFamily: T.mono,
                        fontSize: ".6rem",
                        color: T.dim,
                        marginTop: 2,
                      }}
                    >
                      {opt.desc}
                    </div>
                  </button>
                ))}
              </div>
              <Mono size=".52rem" color={T.dim} style={{ display: "block", marginTop: 8 }}>
                {alertMode === "one_and_done"
                  ? "This watch sends one alert and then stops."
                  : "This watch keeps monitoring after the first alert. You can fine-tune repeat timing in Advanced settings."}
              </Mono>
            </div>

            <div>
              <div className="mono-label">Sensitivity</div>
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                {sensitivityOptions.map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => setConfidenceThreshold(opt.value)}
                    style={{
                      flex: 1,
                      padding: "10px 8px",
                      borderRadius: 6,
                      border:
                        confidenceThreshold === opt.value
                          ? `1px solid ${T.primary}`
                          : `1px solid ${T.borderSoft}`,
                      background:
                        confidenceThreshold === opt.value
                          ? "rgba(0,184,219,0.08)"
                          : T.card2,
                      cursor: "pointer",
                      transition: "all 0.15s",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: T.mono,
                        fontSize: ".78rem",
                        fontWeight: 600,
                        color: confidenceThreshold === opt.value ? T.primary : T.text,
                      }}
                    >
                      {opt.label}
                    </div>
                    <div
                      style={{
                        fontFamily: T.mono,
                        fontSize: ".6rem",
                        color: T.dim,
                        marginTop: 2,
                      }}
                    >
                      {opt.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 0",
              }}
            >
              <div>
                <Mono size=".58rem" color={T.text}>
                  Market hours only
                </Mono>
              </div>
              <button
                type="button"
                onClick={() => setMarketHoursOnly(!marketHoursOnly)}
                style={{
                  width: 36,
                  height: 20,
                  borderRadius: 10,
                  border: `1px solid ${T.borderSoft}`,
                  cursor: "pointer",
                  background: marketHoursOnly ? `${T.primary}20` : T.card,
                  position: "relative",
                  transition: "background 0.2s",
                }}
              >
                <div
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    background: marketHoursOnly ? T.primary : T.dim,
                    position: "absolute",
                    top: 2,
                    left: marketHoursOnly ? 19 : 3,
                    transition: "left 0.2s",
                  }}
                />
              </button>
            </div>

            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: T.mono,
                  fontSize: "0.65rem",
                  color: T.dim,
                  padding: 0,
                  transition: "color 0.15s",
                }}
              >
                {showAdvanced ? "Hide advanced settings" : "Advanced settings"}
              </button>
              {showAdvanced && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 14,
                    borderRadius: 10,
                    border: `1px solid ${T.borderSoft}`,
                    background: T.card2,
                    display: "grid",
                    gap: 12,
                  }}
                >
                  <Mono size=".54rem" color={T.dim} style={{ lineHeight: 1.45 }}>
                    Fine-tune how often Oracle checks this watch. Repeat timing only appears when the watch keeps running after the first alert.
                  </Mono>
                  <div
                    style={{
                      display: "grid",
                      gap: 14,
                      alignItems: "end",
                    }}
                    className={
                      alertMode === "keep_watching"
                        ? "grid-cols-1 sm:grid-cols-2"
                        : "grid-cols-1"
                    }
                  >
                    <div>
                      <div className="mono-label" style={{ marginBottom: 6 }}>
                        Evaluation interval (min)
                      </div>
                      <Input
                        type="number"
                        className="lh-input"
                        value={interval}
                        onChange={(event) => setInterval(parseInt(event.target.value) || 5)}
                        min={5}
                        max={1440}
                      />
                    </div>
                    {alertMode === "keep_watching" && (
                      <div>
                        <div className="mono-label" style={{ marginBottom: 6 }}>
                          Min gap between alerts (min)
                        </div>
                        <Input
                          type="number"
                          className="lh-input"
                          value={cooldown}
                          onChange={(event) => setCooldown(parseInt(event.target.value) || 0)}
                          min={0}
                          max={10080}
                        />
                      </div>
                    )}
                  </div>
                  {alertMode === "one_and_done" && (
                    <Mono size=".5rem" color={T.dim} style={{ lineHeight: 1.4 }}>
                      Repeat settings are off because this watch stops after its first alert.
                    </Mono>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="w-28 h-10"
                onClick={() => closeDialog(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-10"
                onClick={handleReviewWatch}
                disabled={isPreviewLoading || !selection.optionContract}
              >
                {isPreviewLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <BellPlus className="mr-2 h-4 w-4" />
                )}
                {isPreviewLoading ? "Creating..." : "Create Watch"}
              </Button>
            </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
